/**
 * The help panel. Slides over the current screen rather than navigating away —
 * losing a half-finished invoice to ask a question is worse than the question.
 *
 * Three layers, in order: the assistant answers first, the guides back it up
 * (and keep working when the assistant cannot), and the ticket is the honest
 * exit when neither landed.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  X, Send, BookOpen, LifeBuoy, Sparkles, Loader2, ArrowLeft, Check, Megaphone,
  Reply, ImagePlus, Trash2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../lib/api';
import { DRIVE_IQ, DriveIQBadge, DriveIQWordmark } from './DriveIQ';

const TABS = [
  { key: 'ask', label: 'Ask', icon: Sparkles },
  { key: 'guides', label: 'Guides', icon: BookOpen },
  { key: 'new', label: "What's new", icon: Megaphone },
  { key: 'ticket', label: 'Stuck?', icon: LifeBuoy },
];

// Bold runs, and nothing else — the guides and the changelog are written in
// prose with the occasional **lead-in**, not in full markdown.
const richText = (text) => text
  .replace(/([^\n])\n(?!\n)/g, '$1 ')
  .split(/(\*\*[^*]+\*\*)/g)
  .map((chunk, i) => (chunk.startsWith('**') && chunk.endsWith('**')
    ? <strong key={i} className="text-slate-900">{chunk.slice(2, -2)}</strong>
    : <React.Fragment key={i}>{chunk}</React.Fragment>));

const STATUS_TONE = {
  new: 'bg-teal-50 text-teal-700 border-teal-200',
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  answered: 'bg-sky-50 text-sky-700 border-sky-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const newSession = () => (crypto?.randomUUID?.() || String(Date.now())).replace(/-/g, '');

export const HelpPanel = ({ open, onClose }) => {
  const { authHeader } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState('ask');

  // Assistant
  const [sessionId] = useState(newSession);
  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [fellBack, setFellBack] = useState('');
  const scrollRef = useRef(null);

  // Guides
  const [guides, setGuides] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [search, setSearch] = useState('');
  const [article, setArticle] = useState(null);

  // Ticket
  const [ticket, setTicket] = useState({ subject: '', message: '' });
  const [raising, setRaising] = useState(false);
  const [raised, setRaised] = useState('');
  const [ticketErr, setTicketErr] = useState('');
  const [shots, setShots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [openTicket, setOpenTicket] = useState('');
  const [replies, setReplies] = useState({});
  const [replying, setReplying] = useState('');

  // What's new
  const [news, setNews] = useState([]);
  const [unseen, setUnseen] = useState(0);

  const loadTickets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/help/tickets`, { headers: authHeader() });
      setMyTickets(data.tickets || []);
    } catch { setMyTickets([]); }
  }, [authHeader]);

  const loadGuides = useCallback(async (q) => {
    try {
      const { data } = await axios.get(`${API}/help/guides`, {
        // The screen they opened the panel from decides what to lead with.
        params: { q, screen: location.pathname }, headers: authHeader(),
      });
      setGuides(data.guides || []);
      setSuggested(data.suggested || []);
    } catch { setGuides([]); }
  }, [authHeader, location.pathname]);

  useEffect(() => { if (open) loadGuides(''); }, [open, loadGuides]);

  useEffect(() => {
    if (!open) return;
    loadTickets();
    axios.get(`${API}/help/whats-new`, { headers: authHeader() })
      .then(({ data }) => { setNews(data.entries || []); setUnseen(data.unseen || 0); })
      .catch(() => { /* the rest of the panel still works */ });
  }, [open, loadTickets, authHeader]);

  // Opening the tab is the acknowledgement — no "mark as read" to hunt for.
  useEffect(() => {
    if (tab !== 'new' || !unseen) return;
    setUnseen(0);
    axios.post(`${API}/help/whats-new/seen`, {}, { headers: authHeader() })
      .catch(() => { /* it will simply still look unseen next time */ });
  }, [tab, unseen, authHeader]);

  const replyToTicket = async (id) => {
    const message = (replies[id] || '').trim();
    if (message.length < 5) return;
    setReplying(id);
    try {
      await axios.post(`${API}/help/tickets/${id}/reply`, { message },
        { headers: authHeader() });
      setReplies((r) => ({ ...r, [id]: '' }));
      await loadTickets();
    } catch { /* surfaced by the unchanged draft */ } finally { setReplying(''); }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, streaming]);

  const ask = async () => {
    const text = question.trim();
    if (!text || streaming) return;
    setQuestion('');
    setFellBack('');
    setTurns((t) => [...t, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      // fetch, not EventSource — the question goes in a POST body and needs the
      // CSRF header that every other write carries.
      const res = await fetch(`${API}/help/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ session_id: sessionId, message: text, screen: location.pathname }),
      });
      if (!res.ok || !res.body) throw new Error(String(res.status));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.delta) {
            setTurns((t) => {
              const next = [...t];
              next[next.length - 1] = {
                role: 'assistant',
                content: next[next.length - 1].content + payload.delta,
              };
              return next;
            });
          }
          if (payload.error) setFellBack(payload.error);
        }
      }
    } catch {
      setFellBack('The assistant could not answer just now.');
    } finally {
      setStreaming(false);
      // An empty answer helps nobody — drop the placeholder.
      setTurns((t) => (t.length && t[t.length - 1].content === '' ? t.slice(0, -1) : t));
    }
  };

  const openGuide = async (slug) => {
    try {
      const { data } = await axios.get(`${API}/help/guides/${slug}`, { headers: authHeader() });
      setTab('guides');
      setArticle(data);
    } catch { /* ignore */ }
  };

  // A screenshot of the actual screen saves three rounds of "which button did
  // you press". Paste works too — that is how people take screenshots.
  const addShot = async (file) => {
    if (!file || shots.length >= 3) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setTicketErr('Attach a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setTicketErr('That screenshot is over 5 MB. Crop it or save it as a JPEG.');
      return;
    }
    setUploading(true); setTicketErr('');
    const body = new FormData();
    body.append('file', file, file.name || 'screenshot.png');
    try {
      const { data } = await axios.post(`${API}/help/attachments`, body,
        { headers: authHeader() });
      setShots((prev) => [...prev, { ...data, preview: URL.createObjectURL(file) }]);
    } catch (e) {
      setTicketErr(e?.response?.data?.detail || 'That screenshot would not upload.');
    } finally { setUploading(false); }
  };

  const dropShot = async (id) => {
    const shot = shots.find((x) => x.id === id);
    if (shot?.preview) URL.revokeObjectURL(shot.preview);
    setShots((prev) => prev.filter((x) => x.id !== id));
    axios.delete(`${API}/help/attachments/${id}`, { headers: authHeader() })
      .catch(() => { /* already gone, or never bound — harmless either way */ });
  };

  const onPaste = (e) => {
    const file = [...(e.clipboardData?.items || [])]
      .find((i) => i.type.startsWith('image/'))?.getAsFile();
    if (file) { e.preventDefault(); addShot(file); }
  };

  const raise = async () => {
    setRaising(true); setTicketErr('');
    try {
      const { data } = await axios.post(`${API}/help/tickets`, {
        ...ticket,
        session_id: sessionId,
        screen: location.pathname,
        attachment_ids: shots.map((s) => s.id),
      }, { headers: authHeader() });
      setRaised(data.reference);
      setTicket({ subject: '', message: '' });
      shots.forEach((s) => s.preview && URL.revokeObjectURL(s.preview));
      setShots([]);
      loadTickets();
    } catch (e) {
      setTicketErr(e?.response?.data?.detail || 'Could not send that. Please try again.');
    } finally { setRaising(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="help-panel">
      <button
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close help"
        data-testid="help-backdrop"
      />
      <aside className="relative w-full sm:w-[440px] h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <LifeBuoy size={16} className="text-teal-600" />
            <span className="text-sm font-semibold text-slate-900">Help</span>
            {tab === 'ask' && (
              <DriveIQBadge className="ml-1" testId="help-driveiq-badge" />
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="help-close-btn">
            <X size={18} />
          </button>
        </header>

        <nav className="flex border-b border-slate-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setArticle(null); }}
              className={`relative flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold py-2.5 border-b-2 ${
                tab === t.key
                  ? 'border-teal-500 text-teal-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`help-tab-${t.key}`}
            >
              <t.icon size={13} /> {t.label}
              {t.key === 'new' && unseen > 0 && (
                <span
                  className="absolute top-1.5 right-2 h-1.5 w-1.5 rounded-full bg-teal-500"
                  data-testid="help-new-dot"
                />
              )}
            </button>
          ))}
        </nav>

        {/* ---------------- Ask ---------------- */}
        {tab === 'ask' && (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="help-thread">
              {turns.length === 0 && (
                <div className="text-sm text-slate-500">
                  <p className="font-medium text-slate-700">
                    Ask <DriveIQWordmark /> anything about your workshop.
                  </p>
                  <p className="mt-1.5 text-xs">
                    {DRIVE_IQ.tagline} — how to do something, or where your own numbers
                    stand. It can look, never change anything.
                  </p>
                  <div className="mt-3 space-y-1.5">
                    {['How do I issue a lifetime warranty?',
                      'How much is outstanding right now?',
                      'How do I take a deposit on an estimate?'].map((q) => (
                        <button
                          key={q}
                          onClick={() => setQuestion(q)}
                          className="block w-full text-left text-xs text-teal-700 bg-teal-50/70 hover:bg-teal-50 border border-teal-100 rounded-lg px-2.5 py-1.5"
                          data-testid="help-suggestion"
                        >
                          {q}
                        </button>
                      ))}
                  </div>
                  {suggested.length > 0 && (
                    <div className="mt-5" data-testid="help-screen-guides">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        For this screen
                      </div>
                      <div className="mt-1.5 space-y-1">
                        {suggested.map((g) => (
                          <button
                            key={g.slug}
                            onClick={() => openGuide(g.slug)}
                            className="block w-full text-left text-xs text-slate-700 hover:text-teal-700 border border-slate-200 hover:border-teal-400 rounded-lg px-2.5 py-1.5"
                            data-testid={`help-screen-guide-${g.slug}`}
                          >
                            {g.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {turns.map((t, i) => (
                <div
                  key={i}
                  className={t.role === 'user'
                    ? 'ml-8 bg-slate-100 rounded-xl rounded-br-sm px-3 py-2 text-sm text-slate-800'
                    : 'mr-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed'}
                  data-testid={t.role === 'user' ? 'help-user-turn' : 'help-assistant-turn'}
                >
                  {t.content || (streaming && <Loader2 className="animate-spin text-slate-300" size={14} />)}
                </div>
              ))}
              {fellBack && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="help-fallback">
                  {fellBack} The guides and the "Still stuck?" form still work.
                </div>
              )}
            </div>
            <div className="border-t border-slate-200 p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); }
                  }}
                  rows={2}
                  maxLength={2000}
                  placeholder="Type your question…"
                  className="flex-1 resize-none text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  data-testid="help-input"
                />
                <button
                  onClick={ask}
                  disabled={streaming || !question.trim()}
                  className="shrink-0 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white rounded-lg p-2.5"
                  data-testid="help-send-btn"
                >
                  {streaming ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ---------------- Guides ---------------- */}
        {tab === 'guides' && (
          <div className="flex-1 overflow-y-auto">
            {article ? (
              <div className="px-4 py-4" data-testid="help-article">
                <button
                  onClick={() => setArticle(null)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-teal-700 mb-3"
                  data-testid="help-article-back"
                >
                  <ArrowLeft size={13} /> All guides
                </button>
                <h3 className="text-base font-semibold text-slate-900">{article.title}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{article.summary}</p>
                <div className="mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {richText(article.body)}
                </div>
              </div>
            ) : (
              <div className="px-4 py-4">
                <input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); loadGuides(e.target.value); }}
                  placeholder="Search the guides…"
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  data-testid="help-search-input"
                />
                {!search && suggested.length > 0 && (
                  <div className="mb-4" data-testid="help-guides-suggested">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                      For this screen
                    </div>
                    <div className="space-y-1.5">
                      {suggested.map((g) => (
                        <button
                          key={g.slug}
                          onClick={() => openGuide(g.slug)}
                          className="block w-full text-left border border-teal-200 bg-teal-50/60 hover:bg-teal-50 rounded-lg px-3 py-2.5"
                          data-testid={`help-suggested-${g.slug}`}
                        >
                          <div className="text-sm font-medium text-slate-900">{g.title}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{g.summary}</div>
                        </button>
                      ))}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-4 mb-1.5">
                      All guides
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  {guides.map((g) => (
                    <button
                      key={g.slug}
                      onClick={() => openGuide(g.slug)}
                      className="block w-full text-left border border-slate-200 hover:border-teal-400 rounded-lg px-3 py-2.5"
                      data-testid={`help-guide-${g.slug}`}
                    >
                      <div className="text-sm font-medium text-slate-900">{g.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{g.summary}</div>
                    </button>
                  ))}
                  {guides.length === 0 && (
                    <p className="text-sm text-slate-500" data-testid="help-guides-empty">
                      No guide matches that. Try the assistant, or the "Still stuck?" form.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------------- What's new ---------------- */}
        {tab === 'new' && (
          <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="help-whats-new">
            <p className="text-xs text-slate-500 mb-3">
              What Unisol has shipped into Driveline, newest first.
            </p>
            <div className="space-y-4">
              {news.map((n) => (
                <article key={n.id} className="border-l-2 border-teal-200 pl-3" data-testid={`help-news-${n.id}`}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400">
                    <span>{new Date(n.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <span className="text-teal-700">· {n.tag}</span>
                  </div>
                  <h3 className="mt-1 text-sm font-semibold text-slate-900">{n.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                    {richText(n.body)}
                  </p>
                </article>
              ))}
              {news.length === 0 && (
                <p className="text-sm text-slate-500" data-testid="help-news-empty">
                  Nothing new to report just yet.
                </p>
              )}
            </div>
          </div>
        )}

        {/* ---------------- Ticket ---------------- */}
        {tab === 'ticket' && (
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {raised ? (
              <div className="text-center py-6" data-testid="help-ticket-raised">
                <Check className="mx-auto text-emerald-600" size={30} />
                <p className="mt-3 text-sm font-semibold text-slate-900">We have your question</p>
                <p className="mt-1 text-sm text-slate-500">
                  Your reference is <b className="text-slate-700">{raised}</b>. Unisol's
                  reply lands on your account email and in this panel, where you can
                  answer back.
                </p>
                <button
                  onClick={() => setRaised('')}
                  className="mt-4 text-xs font-semibold text-teal-700"
                  data-testid="help-ticket-another"
                >
                  Ask about something else
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-500 mb-3">
                  Send it to the Unisol team. Your conversation above, the screen you are on
                  and your workshop details go with it, so you do not have to explain twice.
                </p>
                {ticketErr && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-1.5 mb-3" data-testid="help-ticket-err">
                    {ticketErr}
                  </div>
                )}
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">What is it about?</label>
                <input
                  value={ticket.subject}
                  onChange={(e) => setTicket((t) => ({ ...t, subject: e.target.value }))}
                  placeholder="e.g. GST export is missing an invoice"
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  data-testid="help-ticket-subject"
                />
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Tell us what happened</label>
                <textarea
                  value={ticket.message}
                  onChange={(e) => setTicket((t) => ({ ...t, message: e.target.value }))}
                  onPaste={onPaste}
                  rows={6}
                  placeholder="What you expected, and what happened instead. Paste a screenshot straight in here if you have one."
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  data-testid="help-ticket-message"
                />

                <div className="mt-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs uppercase tracking-wider text-slate-500">
                      Screenshot of the error
                    </label>
                    <span className="text-[10px] text-slate-400">{shots.length}/3 · optional</span>
                  </div>

                  {shots.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="help-shot-list">
                      {shots.map((s) => (
                        <div key={s.id} className="relative group" data-testid={`help-shot-${s.id}`}>
                          <img
                            src={s.preview}
                            alt={s.filename}
                            className="h-16 w-24 object-cover rounded-lg border border-slate-200"
                          />
                          <button
                            type="button"
                            onClick={() => dropShot(s.id)}
                            title="Remove"
                            className="absolute -top-1.5 -right-1.5 bg-white border border-slate-300 rounded-full p-0.5 text-slate-400 hover:text-red-600"
                            data-testid={`help-shot-remove-${s.id}`}
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {shots.length < 3 && (
                    <label
                      className="mt-2 flex items-center justify-center gap-2 text-xs font-semibold text-slate-500 hover:text-teal-700 border border-dashed border-slate-300 hover:border-teal-400 rounded-lg py-3 cursor-pointer"
                      data-testid="help-shot-add"
                    >
                      {uploading
                        ? <><Loader2 className="animate-spin" size={14} /> Uploading…</>
                        : <><ImagePlus size={14} /> Attach a screenshot, or paste one above</>}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => { addShot(e.target.files?.[0]); e.target.value = ''; }}
                        data-testid="help-shot-input"
                      />
                    </label>
                  )}
                </div>

                <button
                  onClick={raise}
                  disabled={raising || uploading || ticket.subject.trim().length < 3 || ticket.message.trim().length < 10}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-semibold py-2.5 rounded-lg"
                  data-testid="help-ticket-submit"
                >
                  {raising ? <Loader2 className="animate-spin" size={15} /> : <LifeBuoy size={15} />}
                  Send to Unisol
                </button>
              </>
            )}

            {myTickets.length > 0 && (
              <div className="mt-7 border-t border-slate-200 pt-4" data-testid="help-my-tickets">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  Your questions to Unisol
                </div>
                <div className="space-y-2">
                  {myTickets.map((t) => (
                    <div key={t.id} className="border border-slate-200 rounded-lg" data-testid={`help-ticket-${t.reference}`}>
                      <button
                        onClick={() => setOpenTicket(openTicket === t.id ? '' : t.id)}
                        className="w-full text-left px-3 py-2"
                        data-testid={`help-ticket-open-${t.reference}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-[9px] font-semibold uppercase tracking-wider border rounded-full px-1.5 py-0.5 ${STATUS_TONE[t.status]}`}>
                            {t.status === 'answered' ? 'replied' : t.status}
                          </span>
                          <span className="text-xs font-semibold text-slate-900 truncate">{t.subject}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {t.reference} · {(t.thread || []).length} {(t.thread || []).length === 1 ? 'message' : 'messages'}
                        </div>
                      </button>

                      {openTicket === t.id && (
                        <div className="border-t border-slate-100 px-3 py-2.5" data-testid={`help-ticket-thread-${t.reference}`}>
                          <p className="text-xs text-slate-600 whitespace-pre-wrap">{t.message}</p>
                          {(t.attachments || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {t.attachments.map((a) => (
                                <a
                                  key={a.id}
                                  href={`${API}/help/attachments/${a.id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid={`help-ticket-shot-${a.id}`}
                                >
                                  <img
                                    src={`${API}/help/attachments/${a.id}`}
                                    alt={a.filename}
                                    className="h-14 w-20 object-cover rounded border border-slate-200"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                          {(t.thread || []).map((m) => (
                            <div
                              key={m.id}
                              className={m.author === 'unisol'
                                ? 'mt-2.5 bg-teal-50/70 border border-teal-100 rounded-lg px-2.5 py-2'
                                : 'mt-2.5 ml-6 bg-slate-100 rounded-lg px-2.5 py-2'}
                            >
                              <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                                {m.author === 'unisol' ? 'Unisol' : 'You'}
                              </div>
                              <p className="text-xs text-slate-700 whitespace-pre-wrap mt-0.5">{m.message}</p>
                            </div>
                          ))}

                          {t.status !== 'closed' ? (
                            <>
                              <textarea
                                value={replies[t.id] || ''}
                                onChange={(e) => setReplies((r) => ({ ...r, [t.id]: e.target.value }))}
                                rows={3}
                                placeholder="Reply to Unisol…"
                                className="mt-3 w-full text-xs border border-slate-300 rounded-lg px-2.5 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                                data-testid={`help-ticket-reply-input-${t.reference}`}
                              />
                              <button
                                onClick={() => replyToTicket(t.id)}
                                disabled={replying === t.id || (replies[t.id] || '').trim().length < 5}
                                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white rounded-lg px-3 py-1.5"
                                data-testid={`help-ticket-reply-send-${t.reference}`}
                              >
                                {replying === t.id ? <Loader2 className="animate-spin" size={12} /> : <Reply size={12} />}
                                Send
                              </button>
                            </>
                          ) : (
                            <p className="mt-3 text-[11px] text-slate-400">
                              This one is closed. Raise a new question above if it comes back.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

export default HelpPanel;
